// src/Settings.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { FiMail, FiEye, FiEyeOff } from "react-icons/fi";
import SharedLayout from "./SharedLayout";
import InfoModal from "./modals/InfoModals";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_COVER =
  "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1600&auto=format&fit=crop";

const emailOk = (e) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e).toLowerCase());
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const knownCodes = ["90", "1", "44"];

const SettingsPage = () => {
  const { userId } = useParams();
  const [showPassword, setShowPassword] = useState(false);

  const [phoneCode, setPhoneCode] = useState("90");
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    number: "",
    password: "",
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // InfoModal
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const openInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // Kullanıcıyı getir (userId varsa)
  const fetchUser = useCallback(async () => {
    if (!userId) return;
    if (!BACKEND_URL) {
      openInfo("error", "Yapılandırma Hatası", "BACKEND_URL tanımlı değil.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/admin/${userId}`);

      if (!response.data) throw new Error("Sunucudan veri gelmedi");

      const {
        name = "",
        surname = "",
        email = "",
        number = "",
      } = response.data;

      // Gelen telefon numarasını (+ işareti vb.) normalize et ve code + body olarak ayır
      const digits = onlyDigits(number);
      let code = phoneCode;
      let body = digits;

      // Bilinen ülke kodlarından biriyle başlıyorsa ayır
      const matchCode = knownCodes.find((c) => digits.startsWith(c));
      if (matchCode) {
        code = matchCode;
        body = digits.slice(matchCode.length);
      }

      setPhoneCode(code || "90");
      setFormData({
        name,
        surname,
        email,
        number: body,
        password: "",
      });
    } catch (error) {
      console.error("Error fetching user data:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      openInfo(
        "error",
        "Yükleme Hatası",
        "Kullanıcı verileri alınamadı. Lütfen tekrar deneyin."
      );
    } finally {
      setLoading(false);
    }
  }, [userId, phoneCode]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Kaydet
  const handleUpdate = async (e) => {
    e.preventDefault();

    // Basit validasyonlar
    if (!formData.name.trim() || !formData.surname.trim()) {
      openInfo(
        "warning",
        "Eksik Bilgi",
        "Lütfen ad ve soyad bilgilerinizi girin."
      );
      return;
    }
    if (!formData.email.trim() || !emailOk(formData.email)) {
      openInfo(
        "warning",
        "Geçersiz E-posta",
        "Geçerli bir e-posta adresi girin."
      );
      return;
    }

    // Telefonu E.164 benzeri: code + digits (artı işareti olmadan)
    const numberDigits = onlyDigits(formData.number);
    const codeDigits = onlyDigits(phoneCode || "90");
    const combinedNumber = numberDigits ? `${codeDigits}${numberDigits}` : "";

    // Şifre boş ise payload’a koymayalım
    const payload = {
      name: formData.name.trim(),
      surname: formData.surname.trim(),
      email: formData.email.trim().toLowerCase(),
      number: combinedNumber,
      ...(formData.password ? { password: formData.password } : {}),
    };

    try {
      setSaving(true);
      await axios.put(`${BACKEND_URL}/admin/${userId}`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      openInfo("success", "Başarılı", "Ayarlar başarıyla kaydedildi.");
      setFormData((p) => ({ ...p, password: "" })); // şifre alanını sıfırla
      fetchUser();
    } catch (err) {
      console.error("Error updating user:", err);
      const msg = err?.response?.data?.message || "Güncelleme başarısız oldu.";
      openInfo("error", "Hata", msg);
    } finally {
      setSaving(false);
    }
  };

  const initials = (
    `${formData.name?.trim()?.[0] || ""}${
      formData.surname?.trim()?.[0] || ""
    }` || "KJ"
  ).toUpperCase();

  return (
    <SharedLayout pageTitle="AYARLAR">
      {/* Cover Image */}
      <div className="relative w-full h-40 sm:h-48 md:h-56 lg:h-64">
        <img
          src={DEFAULT_COVER}
          alt="Cover"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      {/* Profile Section */}
      <div className="relative px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start">
            {/* Profile Image */}
            <div className="relative -mt-12 sm:-mt-16 mr-4 sm:mr-6">
              <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-4 border-white bg-gray-300 overflow-hidden">
                <div className="w-full h-full flex items-center justify-center text-gray-600">
                  <span className="text-2xl sm:text-3xl font-semibold">
                    {initials}
                  </span>
                </div>
              </div>
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold mt-2 sm:mt-4">
              AYARLAR
            </h1>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-6">
            <button
              className="px-6 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition"
              onClick={fetchUser}
              disabled={loading || saving}
            >
              İptal
            </button>
            <button
              className={`px-6 py-2 rounded-full text-white transition ${
                saving
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600"
              }`}
              onClick={handleUpdate}
              disabled={loading || saving}
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>

        <h2 className="text-sm font-medium text-gray-500 mb-6 mt-4">Profil</h2>

        <div className="max-w-3xl space-y-6">
          {/* First Name & Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adınız
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Soyadınız
              </label>
              <input
                type="text"
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Adresiniz
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiMail className="text-gray-400" />
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefon Numaranız
            </label>
            <div className="flex">
              <select
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value)}
                className="w-28 sm:w-32 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="90">+90 TR</option>
                <option value="1">+1 US</option>
                <option value="44">+44 UK</option>
              </select>
              <input
                type="tel"
                name="number"
                placeholder="5000000000"
                value={formData.number}
                onChange={handleChange}
                className="flex-1 px-4 py-2 border-t border-r border-b border-gray-300 rounded-r-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Kaydederken numara <code>ÜlkeKodu + Numara</code> (örn:{" "}
              <code>90500...</code>) formatında gönderilir.
            </p>
          </div>

          {/* Password */}
          <div>
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yeni şifre oluştur
              </label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <button
                type="button"
                aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
              <p className="mt-1 text-xs text-gray-500">
                Boş bırakırsanız şifreniz değişmez.
              </p>
            </div>
          </div>

          {/* File Upload (görsel) – backend entegrasyonuna göre bağlanabilir */}
          <div>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600 justify-center gap-1">
                  <label className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none">
                    <span>Yüklemek için tıklayınız</span>
                    <input type="file" className="sr-only" />
                  </label>
                  <span>veya sürükleyin ve bırakın</span>
                </div>
                <p className="text-xs text-gray-500">
                  SVG, PNG, JPG or GIF (max. 800x400px)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Modal */}
      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />

      {/* Yükleniyor overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-4 shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      )}
    </SharedLayout>
  );
};

export default SettingsPage;
